import { useRef, useState } from 'react';
import { ParentShell } from '@/routes/_app.parent';
import { MyConnectQr } from '@/components/contacts/my-connect-qr';
import { PlayerShell } from '@/routes/_app.player';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { FORMATION_TYPES, TUTOR_KINDS, normalizeTutorKind } from '@jeraisonne/db';
import type { FormationType, SchoolLevel, SubjectSlug, TutorKind } from '@jeraisonne/db';
import type { RegionCode } from '@jeraisonne/db/live';
import { Button, Input, Label, Alert, AlertDescription, Card, CardContent } from '@jeraisonne/ui';
import { LogOut, ChevronRight, CalendarClock, Gift, Mic } from 'lucide-react';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  PSEUDO_MAX,
  profileSaveErrorMessage,
  pseudoIssue,
  suggestPseudo,
} from '@/lib/student-pseudo';
import { supabase } from '@/lib/supabase';
import {
  updateProfile,
  uploadAvatar,
  getTutorProfile,
  updateTutorProfile,
  getTutorApiKeyStatus,
} from '@/lib/profile';
import { AvatarCircle } from '@/components/avatar-circle';
import { StudentLevelControl } from '@/components/student-level-control';
import { StudentGeoControl } from '@/components/student-geo-control';
import { StudentSchoolControl, type CurrentSchool } from '@/components/student-school-control';
import { StudentSubjectsControl } from '@/components/student-subjects-control';
import { AcademicTutorControl } from '@/components/academic-tutor-control';
import { useOffersAcademicMentoring, useSetOffersAcademicMentoring } from '@/lib/tutor/rendezvous';
import { needsSubjectsReview } from '@/lib/subjects-review';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { NotificationPreferencesPanel } from '@/components/notification-preferences-panel';
import { CollapsiblePanel } from '@/components/collapsible-panel';
import {
  ACCOUNT_TYPE_CHANGE_HREF,
  ACCOUNT_TYPE_LABELS,
  useAccountTypeChangeAllowed,
} from '@/lib/account-type-change';
import { ThemePreferenceControl } from '@/components/theme-preference-control';
import { StudentSubscriptionBlock } from '@/components/student-subscription-block';
import { StudentBottomNav } from '@/components/student-bottom-nav';
import { TutorSidebar, TutorBottomNav } from '@/components/tutor-nav';
import { DeleteAccountModal } from '@/features/account-deletion/delete-account-modal';
import { BlockedUsersPanel } from '@/features/safety/blocked-users-panel';
import { SpeakerSpritesEditor } from '@/features/animations/speaker-sprites-editor';
import { SPEAKER_PAGE, useCanRecordVoices } from '@/lib/animations/speaker';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_AVATAR_SIZE_MB = 2;

type StudentProfileRow = {
  level: SchoolLevel | null;
  subjects: SubjectSlug[];
  level_changed_at: string | null;
  subjects_reviewed_at: string | null;
  country: string | null;
  region: RegionCode | null;
  departement: string | null;
  is_higher_ed_student: boolean;
  formation_type: FormationType | null;
  study_year: number | null;
  is_alternant: boolean;
  academic_tutor_id: string | null;
  academic_tutor: { display_name: string } | null;
  /** Vrai nom (protégé, migration 0661) : vu seulement par ses profs et ses parents. */
  real_name: string | null;
  /** Établissement choisi dans l'annuaire (migration 0705). */
  school_id: string | null;
  school: CurrentSchool | null;
};

function AcademicMentoringToggle({ userId }: { userId: string }) {
  const { data: enabled } = useOffersAcademicMentoring(userId);
  const setEnabled = useSetOffersAcademicMentoring(userId);

  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(enabled)}
      disabled={setEnabled.isPending}
      onClick={() => setEnabled.mutate(!enabled)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
        enabled ? 'bg-primary-500' : 'bg-ink-200'
      }`}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
          enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// ─── Profile page ─────────────────────────────────────────────────────────────

export function ProfilePage() {
  const { user, profile, refreshProfile } = useAuth();
  const accountTypeChange = useAccountTypeChangeAllowed(user?.id, profile?.role);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  // Élève : vrai nom (protégé) ; null = pas encore modifié (valeur du serveur).
  const [realName, setRealName] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [replacingKey, setReplacingKey] = useState(false);
  const [removeKey, setRemoveKey] = useState(false);
  const [bio, setBio] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [tutorKind, setTutorKind] = useState<TutorKind>('creator');
  const tutorKindHydrated = useRef(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const admin = isAdmin(profile, user?.email);
  const speaker = useCanRecordVoices();
  const isTutor = profile?.role === 'tutor' || admin;
  const isStudent = profile?.role === 'student';

  const handleSignOut = async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  };

  const push = usePushNotifications();

  // Fetch student profile for level/subjects display.
  // `level_changed_at`/`is_higher_ed_student`/`formation_type`/`study_year`/
  // `is_alternant`/`academic_tutor_id` (migrations 0151/0237) sont postérieurs
  // aux types générés (stale, cf. CLAUDE.md) → client générique + typage à la
  // main. ⚠️ student_profiles a 2 FK vers profiles depuis 0237 (id_fkey +
  // academic_tutor_id_fkey) → désambiguïser les 2 embeds.
  const { data: studentProfile } = useQuery({
    queryKey: ['student-profile', user?.id],
    enabled: isStudent && !!user?.id,
    queryFn: async (): Promise<StudentProfileRow | null> => {
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .from('student_profiles')
        .select(
          'level, subjects, level_changed_at, subjects_reviewed_at, country, region, departement, is_higher_ed_student, formation_type, study_year, is_alternant, academic_tutor_id, real_name, academic_tutor:profiles!student_profiles_academic_tutor_id_fkey(display_name:visible_name), school_id, school:schools!student_profiles_school_id_fkey(id, name, city, departement, kind, sector)',
        )
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as StudentProfileRow | null;
    },
  });

  // Fetch tutor public-profile fields (bio/description/website/type).
  // ⚠️ NE lit PAS la clé API : elle est write-only (migration 0190). Son état
  // vient de la RPC dédiée ci-dessous.
  const { data: tutorProfileData } = useQuery({
    queryKey: ['tutor-profile', user?.id],
    enabled: isTutor && !!user?.id,
    queryFn: () => getTutorProfile(supabase, user!.id),
    select: (data) => {
      // Pre-fill fields on first load only (when local state is still empty)
      if (data?.bio && !bio) setBio(data.bio);
      if (data?.description && !description) setDescription(data.description);
      if (data?.website_url && !websiteUrl) setWebsiteUrl(data.website_url);
      // Type de prof : hydrate une seule fois (défaut 'creator' ⇒ pas de sentinelle
      // « vide » possible), pour ne pas écraser un choix en cours au refetch.
      if (!tutorKindHydrated.current) {
        setTutorKind(normalizeTutorKind(data?.tutor_kind));
        tutorKindHydrated.current = true;
      }
      return data;
    },
  });

  // État de la clé API (has_key + last4) — jamais la clé brute.
  const { data: apiKeyStatus } = useQuery({
    queryKey: ['tutor-api-key-status', user?.id],
    enabled: isTutor && !!user?.id,
    queryFn: () => getTutorApiKeyStatus(supabase),
  });
  const hasApiKey = apiKeyStatus?.has_key ?? false;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Non connecté');

      // 1. Upload avatar if a new file was picked
      let newAvatarUrl: string | undefined;
      if (pendingFile) {
        newAvatarUrl = await uploadAvatar(supabase, user.id, pendingFile);
      }

      // Élève : le pseudo ne peut pas être le vrai nom (le serveur revérifie).
      if (isStudent) {
        const issue = pseudoIssue(displayName, realName ?? studentProfile?.real_name);
        if (issue) throw new Error(issue);
      }

      // 2. Update base profile
      const profileUpdates: { display_name?: string; avatar_url?: string } = {};
      if (displayName.trim() && displayName.trim() !== profile?.display_name) {
        profileUpdates.display_name = displayName.trim();
      }
      if (newAvatarUrl) {
        profileUpdates.avatar_url = newAvatarUrl;
      }
      if (Object.keys(profileUpdates).length > 0) {
        try {
          await updateProfile(supabase, user.id, profileUpdates);
        } catch (e) {
          throw new Error(profileSaveErrorMessage((e as Error)?.message ?? ''));
        }
      }

      // 2 bis. Élève : vrai nom (vu seulement par ses profs et ses parents).
      if (isStudent && realName !== null) {
        const next = realName.trim() || null;
        if (next !== (studentProfile?.real_name ?? null)) {
          const { error } = await (supabase as unknown as SupabaseClient)
            .from('student_profiles')
            .update({ real_name: next })
            .eq('id', user.id);
          if (error) throw new Error(profileSaveErrorMessage(error.message));
          queryClient.invalidateQueries({ queryKey: ['student-profile', user.id] });
        }
      }

      // 3. Update tutor-specific fields
      if (isTutor) {
        const tutorUpdates: Parameters<typeof updateTutorProfile>[2] = {};
        const currentBio = tutorProfileData?.bio ?? '';
        const currentDesc = tutorProfileData?.description ?? '';
        const currentWeb = tutorProfileData?.website_url ?? '';
        const currentKind = normalizeTutorKind(tutorProfileData?.tutor_kind);
        // Clé API write-only : on n'envoie QUE l'intention (nouvelle clé / suppression).
        if (removeKey) tutorUpdates.anthropic_api_key = null;
        else if (apiKey.trim()) tutorUpdates.anthropic_api_key = apiKey.trim();
        if (bio !== currentBio) tutorUpdates.bio = bio.trim() || null;
        if (description !== currentDesc) tutorUpdates.description = description.trim() || null;
        if (websiteUrl !== currentWeb) tutorUpdates.website_url = websiteUrl.trim() || null;
        if (tutorKind !== currentKind) tutorUpdates.tutor_kind = tutorKind;
        if (Object.keys(tutorUpdates).length > 0) {
          await updateTutorProfile(supabase, user.id, tutorUpdates);
        }
      }
    },
    onSuccess: async () => {
      await refreshProfile();
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['tutor-api-key-status', user?.id] });
      setPendingFile(null);
      setPreviewUrl(null);
      // Réinitialise l'état write-only de la clé (la nouvelle valeur n'est jamais relue).
      setApiKey('');
      setShowApiKey(false);
      setReplacingKey(false);
      setRemoveKey(false);
      setSuccessMsg('Profil mis à jour.');
      setTimeout(() => setSuccessMsg(null), 3000);
    },
  });

  if (!profile || !user) return null;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFileError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setFileError('Format non supporté. Utilise JPEG, PNG, WebP ou GIF.');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE_MB * 1024 * 1024) {
      setFileError(`L'image ne doit pas dépasser ${MAX_AVATAR_SIZE_MB} Mo.`);
      return;
    }
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  const currentAvatarSrc = previewUrl ?? profile.avatar_url;

  const pageContent = (
    <div
      className={`native:pt-[calc(env(safe-area-inset-top)+2rem)] mx-auto max-w-lg space-y-6 px-4 py-8 ${
        isStudent || isTutor || profile.role === 'parent' || profile.role === 'player'
          ? 'pb-24 md:pb-8'
          : ''
      }`}
    >
      {/* Header — élève : le Profil est un onglet de la barre du bas, le retour
          ne sert que sur ordinateur (pas de barre latérale ici), sur sa propre ligne.
          Parent et curieux : coquille avec navigation, pas de retour. */}
      <div className={isStudent ? 'flex flex-col items-start gap-1' : 'flex items-center gap-3'}>
        {profile.role !== 'parent' && profile.role !== 'player' && (
          <button
            type="button"
            className={
              isStudent
                ? 'text-ink-500 hover:text-ink-900 hidden min-h-[2.75rem] items-center text-sm transition-colors md:inline-flex'
                : 'text-ink-500 hover:text-ink-900 text-sm transition-colors'
            }
            onClick={() => {
              if (profile.role === 'student') navigate({ to: '/student/dashboard' });
              else if (profile.role === 'tutor') navigate({ to: '/tutor' });
              else if (profile.role === 'parent') navigate({ to: '/parent' });
              else navigate({ to: '/tutor/dashboard' });
            }}
          >
            {isStudent ? '← Accueil' : '← Retour'}
          </button>
        )}
        <h1 className="text-ink-900 text-2xl font-bold">Mon profil</h1>
      </div>

      {/* Feedback */}
      {saveMutation.isError && (
        <Alert variant="error">
          <AlertDescription>
            {(saveMutation.error as Error)?.message ?? 'Une erreur est survenue.'}
          </AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert variant="success">
          <AlertDescription>{successMsg}</AlertDescription>
        </Alert>
      )}

      <Card className="border-ink-100">
        <CardContent className="space-y-6 p-6">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-3">
            <AvatarCircle name={profile.display_name} src={currentAvatarSrc} size="xl" />
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              className="sr-only"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Changer la photo
            </Button>
            {fileError && <p className="text-xs text-red-600">{fileError}</p>}
          </div>

          {/* Display name — élève : pseudo public + vrai nom protégé (0661-0663) */}
          {isStudent ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="display-name">Mon pseudo</Label>
                <div className="flex gap-2">
                  <Input
                    id="display-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Ton pseudo"
                    maxLength={PSEUDO_MAX}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={suggesting}
                    onClick={async () => {
                      setSuggesting(true);
                      const next = await suggestPseudo();
                      if (next) setDisplayName(next);
                      setSuggesting(false);
                    }}
                    className="min-h-[2.75rem] shrink-0"
                  >
                    Autre pseudo
                  </Button>
                </div>
                <p className="text-ink-400 text-xs">
                  C’est le nom que voient les autres élèves : classements, défis, camarades.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="real-name">Mon vrai nom</Label>
                <Input
                  id="real-name"
                  value={realName ?? studentProfile?.real_name ?? ''}
                  onChange={(e) => setRealName(e.target.value)}
                  placeholder="Prénom et nom"
                  maxLength={80}
                />
                <p className="text-ink-400 text-xs">
                  Visible seulement par tes profs et tes parents.
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="display-name">Nom affiché</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ton prénom ou pseudo"
                maxLength={60}
              />
            </div>
          )}

          {/* Informations scolaires */}
          {isStudent && studentProfile && (
            <div className="border-ink-100 space-y-3 border-t pt-4">
              <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
                Informations scolaires
              </p>
              <StudentLevelControl
                userId={user.id}
                currentLevel={studentProfile.level}
                levelChangedAt={studentProfile.level_changed_at}
                formationType={studentProfile.formation_type}
              />
              <StudentSchoolControl
                userId={user.id}
                currentLevel={studentProfile.level}
                currentSchool={studentProfile.school}
              />
              <StudentGeoControl
                key={`${studentProfile.region ?? ''}-${studentProfile.departement ?? ''}`}
                userId={user.id}
                currentCountry={studentProfile.country}
                currentRegion={studentProfile.region}
                currentDepartement={studentProfile.departement}
              />
              <p className="text-ink-400 text-xs italic">
                Tu peux changer ton niveau une fois par mois.
              </p>
              {needsSubjectsReview(
                studentProfile.subjects_reviewed_at,
                studentProfile.level_changed_at,
                new Date(),
              ) && (
                <Alert variant="info">
                  <AlertDescription>
                    Nouvelle année, nouveau niveau : vérifie tes matières ci-dessous.
                  </AlertDescription>
                </Alert>
              )}
              <StudentSubjectsControl
                userId={user.id}
                currentLevel={studentProfile.level}
                currentSubjects={studentProfile.subjects ?? []}
              />
            </div>
          )}

          {/* Profil Étudiant post-bac (orthogonal au niveau collège/lycée) */}
          {isStudent && studentProfile?.is_higher_ed_student && (
            <div className="border-ink-100 space-y-3 border-t pt-4">
              <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
                Études supérieures
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-ink-600 text-sm">Formation</span>
                <span className="text-ink-900 text-sm font-semibold">
                  {FORMATION_TYPES.find((f) => f.value === studentProfile.formation_type)?.label ??
                    '—'}
                  {studentProfile.study_year ? ` · ${studentProfile.study_year}e année` : ''}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-ink-600 text-sm">Alternance</span>
                <span className="text-ink-900 text-sm font-semibold">
                  {studentProfile.is_alternant ? 'Oui' : 'Non'}
                </span>
              </div>
              {studentProfile.is_alternant && (
                <AcademicTutorControl
                  userId={user.id}
                  currentTutorId={studentProfile.academic_tutor_id}
                  currentTutorName={studentProfile.academic_tutor?.display_name ?? null}
                />
              )}
            </div>
          )}

          {/* Tutor-specific settings */}
          {isTutor && (
            <div className="border-ink-100 space-y-3 border-t pt-4">
              <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
                Fiche publique
              </p>
              <p className="text-ink-500 text-xs">
                Ces informations sont visibles par tes élèves et par toute personne qui ouvre ta
                fiche.
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="tutor-kind">Type de profil</Label>
                <select
                  id="tutor-kind"
                  value={tutorKind}
                  onChange={(e) => setTutorKind(e.target.value as TutorKind)}
                  className="border-ink-200 focus:border-primary-500 focus:ring-primary-500/20 text-ink-900 flex min-h-11 w-full rounded-md border bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
                >
                  {TUTOR_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
                <p className="text-ink-400 text-xs">
                  {TUTOR_KINDS.find((k) => k.value === tutorKind)?.description}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bio">Bio (phrase d'accroche)</Label>
                <Input
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Ex. : Prof de maths au lycée, passionné de pédagogie active."
                  maxLength={280}
                />
                <p className="text-ink-400 text-right text-xs">{bio.length}/280</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description">À propos</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décris ton parcours, ta pédagogie, tes disponibilités…"
                  maxLength={4000}
                  rows={6}
                  className="border-ink-200 focus:border-primary-500 focus:ring-primary-500/20 placeholder:text-ink-300 text-ink-900 flex w-full rounded-md border bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
                />
                <p className="text-ink-400 text-right text-xs">{description.length}/4000</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="website-url">Site web (optionnel)</Label>
                <Input
                  id="website-url"
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://..."
                  maxLength={500}
                />
              </div>

              <p className="text-ink-400 border-ink-100 mt-4 border-t pt-3 text-xs font-semibold uppercase tracking-wider">
                Paramètres professeur
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="api-key">Clé API Anthropic personnelle</Label>

                {/* État de la clé — jamais la valeur brute (write-only, migration 0190). */}
                <div className="border-ink-200 bg-ink-50 text-ink-600 flex min-h-11 items-center rounded-md border px-3 font-mono text-sm">
                  {hasApiKey && !removeKey
                    ? `sk-ant-…${apiKeyStatus?.last4 ?? ''}`
                    : removeKey
                      ? 'La clé sera supprimée à l’enregistrement'
                      : 'Aucune clé — clé système partagée utilisée'}
                </div>

                {hasApiKey && !replacingKey && !removeKey && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setReplacingKey(true)}
                    >
                      Remplacer
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRemoveKey(true)}
                      className="text-error-600"
                    >
                      Supprimer
                    </Button>
                  </div>
                )}

                {removeKey && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRemoveKey(false)}
                  >
                    Annuler la suppression
                  </Button>
                )}

                {(!hasApiKey || replacingKey) && !removeKey && (
                  <div className="flex gap-2">
                    <Input
                      id="api-key"
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-ant-…  (optionnel)"
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApiKey((v) => !v)}
                      className="shrink-0"
                    >
                      {showApiKey ? 'Masquer' : 'Voir'}
                    </Button>
                  </div>
                )}

                <p className="text-ink-400 text-xs">
                  Laisse vide pour utiliser la clé partagée de la plateforme. Par sécurité, une clé
                  enregistrée n’est plus jamais réaffichée — tu peux seulement la remplacer ou la
                  supprimer.
                </p>
              </div>
            </div>
          )}

          {/* Mentorat académique (entretiens gratuits alternance) */}
          {isTutor && (
            <div className="border-ink-100 space-y-2 border-t pt-4">
              <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
                Mentorat académique
              </p>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 text-sm font-semibold">
                    Proposer des entretiens gratuits
                  </p>
                  <p className="text-ink-500 mt-0.5 text-xs">
                    Des étudiants alternants pourront te choisir comme tuteur académique et réserver
                    des entretiens en visio, gratuitement.
                  </p>
                </div>
                <AcademicMentoringToggle userId={user.id} />
              </div>
            </div>
          )}

          {/* Type de compte : tout le monde peut en changer, sauf un mineur (0313) */}
          {accountTypeChange.allowed && profile && (
            <div className="border-ink-100 space-y-3 border-t pt-4">
              <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
                Type de compte
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-ink-900 text-sm font-medium">
                    {ACCOUNT_TYPE_LABELS[profile.role] ?? profile.role}
                  </p>
                  <p className="text-ink-500 text-xs">
                    Tu peux passer en élève, étudiant, parent, professeur ou curieux.
                  </p>
                </div>
                <a
                  href={ACCOUNT_TYPE_CHANGE_HREF}
                  className="border-primary-200 text-primary-700 hover:bg-primary-50 flex min-h-[2.75rem] w-full items-center justify-center rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98] sm:w-auto"
                >
                  Changer de type de compte
                </a>
              </div>
            </div>
          )}

          {/* Mon QR code : se relier en le scannant (élève, prof, parent — 0334) */}
          {profile && profile.role !== 'player' && (
            <CollapsiblePanel
              title="Mon QR code"
              summary="Pour te relier à un camarade, ton prof ou ton parent en un scan"
            >
              <MyConnectQr userId={user.id} />
            </CollapsiblePanel>
          )}

          {/* Utilisateurs bloqués (0604, App Review 1.2) : les débloquer */}
          {profile && profile.role !== 'player' && !user.is_anonymous && (
            <CollapsiblePanel
              title="Utilisateurs bloqués"
              summary="Les personnes avec qui tu ne peux plus échanger"
            >
              <BlockedUsersPanel />
            </CollapsiblePanel>
          )}

          {/* Collection de trousses : roue du jour, cadeaux (0738) */}
          {!user.is_anonymous && (
            <Link
              to="/trousses"
              className="border-ink-200 hover:bg-ink-50 flex min-h-11 items-center gap-3 rounded-2xl border bg-white px-4 py-3 active:scale-[0.98]"
            >
              <Gift className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block text-sm font-semibold">Mes trousses</span>
                <span className="text-ink-500 block text-xs">
                  Ta collection, la roue du jour et les trousses à offrir
                </span>
              </span>
            </Link>
          )}

          {/* Voix des animations : comptes « speaker » (0678) */}
          {speaker && (
            <Link
              to={SPEAKER_PAGE}
              className="border-ink-200 hover:bg-ink-50 flex min-h-11 items-center gap-3 rounded-2xl border bg-white px-4 py-3 active:scale-[0.98]"
            >
              <Mic className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block text-sm font-semibold">Audios animations</span>
                <span className="text-ink-500 block text-xs">
                  Prête ta voix aux animations des SKILLCARDS
                </span>
              </span>
            </Link>
          )}

          {/* Avatar animé des animations SKILLCARD (admin, 0675) */}
          {admin && (
            <CollapsiblePanel
              title="Mon avatar animé"
              summary="L’avatar qui parle dans les animations quand c’est ta voix"
            >
              <SpeakerSpritesEditor userId={user.id} />
            </CollapsiblePanel>
          )}

          {/* Apparence : clair / sombre (appliqué tout de suite, mémorisé sur l'appareil) */}
          <div className="border-ink-100 space-y-3 border-t pt-4">
            <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">Apparence</p>
            <ThemePreferenceControl />
          </div>

          {/* Notifications : push + préférences par catégorie, repliées par défaut */}
          <CollapsiblePanel
            title="Notifications"
            summary={
              push.state === 'subscribed'
                ? 'Push activées · choisis ce que tu reçois'
                : 'Choisis ce que tu reçois par push et par email'
            }
          >
            {push.state !== 'unsupported' && (
              <div className="space-y-3">
                {push.state === 'denied' ? (
                  <p className="text-ink-500 text-sm">
                    Les notifications sont bloquées dans les réglages de ton navigateur.
                  </p>
                ) : (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-ink-900 text-sm font-medium">Notifications push</p>
                      <p className="text-ink-400 text-xs">
                        {push.state === 'subscribed'
                          ? 'Tu recevras les alertes de devoirs, invitations et résultats.'
                          : 'Reçois une alerte pour tes devoirs, invitations et résultats.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      // On NE désactive PAS pendant l'état d'init 'loading' : si le
                      // check de permission natif traîne/échoue, le toggle doit
                      // rester tappable pour déclencher le vrai flow (sinon
                      // « bouton non réactif »). Seule une opération EN COURS
                      // (isLoading) le désactive brièvement.
                      disabled={push.isLoading}
                      onClick={push.state === 'subscribed' ? push.unsubscribe : push.subscribe}
                      className={[
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-offset-2',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        push.state === 'subscribed'
                          ? 'bg-accent-400 focus:ring-accent-400'
                          : 'bg-ink-200 focus:ring-ink-400',
                      ].join(' ')}
                      role="switch"
                      aria-checked={push.state === 'subscribed'}
                    >
                      <span
                        className={[
                          'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform duration-200',
                          push.state === 'subscribed' ? 'translate-x-5' : 'translate-x-0',
                        ].join(' ')}
                      />
                    </button>
                  </div>
                )}
                {push.error && <p className="text-accent-600 text-xs">{push.error}</p>}
              </div>
            )}

            {/* Préférences par catégorie (push / email) — migration 0284 */}
            {user?.id && !user.is_anonymous && (
              <div className="space-y-3">
                <p className="text-ink-900 text-sm font-medium">Ce que je reçois</p>
                <NotificationPreferencesPanel userId={user.id} role={profile?.role} />
              </div>
            )}
          </CollapsiblePanel>

          {/* Save */}
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !displayName.trim()}
            className="w-full"
          >
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </Button>
        </CardContent>
      </Card>

      {/* Subscription — students only */}
      {isStudent && (
        <div className="space-y-3">
          <p className="text-ink-400 px-1 text-xs font-semibold uppercase tracking-wider">
            Mon abonnement
          </p>
          <StudentSubscriptionBlock />
        </div>
      )}

      {/* Tutor — raccourci agenda (les anciens « Paramètres » sont sur cette page) */}
      {isTutor && (
        <div className="space-y-2">
          <Link
            to="/tutor/rendez-vous"
            className="border-ink-100 hover:border-primary-300 flex items-center gap-3 rounded-2xl border bg-white p-4 transition-all active:scale-[.98]"
          >
            <div className="bg-primary-50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <CalendarClock className="text-primary-600 h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-ink-900 text-sm font-semibold">Rendez-vous</p>
              <p className="text-ink-500 mt-0.5 text-xs">Créneaux, réservations et paiements</p>
            </div>
            <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" />
          </Link>
        </div>
      )}

      {/* Sign out */}
      <div className="border-ink-100 border-t pt-4">
        <Button
          variant="ghost"
          className="text-accent-600 hover:text-accent-700 hover:bg-accent-50 w-full gap-2"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
          Se déconnecter
        </Button>
      </div>

      {/*
       * Zone dangereuse — suppression de compte (RGPD art. 17 + Apple
       * Guideline 5.1.1(v)). Volontairement TOUT en bas et visuellement
       * différenciée pour éviter un clic accidentel.
       */}
      <div className="pt-6">
        <p className="text-ink-400 mb-2 px-1 text-xs font-semibold uppercase tracking-wider">
          Zone dangereuse
        </p>
        <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4">
          <p className="text-sm font-semibold text-red-900">Supprimer mon compte</p>
          <p className="mt-1 text-xs text-red-800/80">
            Cette action est irréversible. Ton profil, ta progression et tes données personnelles
            seront définitivement effacés.
          </p>
          <button
            type="button"
            onClick={() => setDeleteAccountOpen(true)}
            className="mt-3 w-full rounded-xl border border-red-300 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 active:scale-[0.98]"
          >
            Supprimer mon compte
          </button>
        </div>
      </div>

      {deleteAccountOpen && <DeleteAccountModal onClose={() => setDeleteAccountOpen(false)} />}

      {isStudent && <StudentBottomNav />}
    </div>
  );

  if (isTutor) {
    return (
      <div className="bg-ink-50 flex h-dvh overflow-hidden">
        <TutorSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1 overflow-y-auto pb-20 md:pb-0">{pageContent}</main>
          <TutorBottomNav />
        </div>
      </div>
    );
  }

  if (profile.role === 'parent') return <ParentShell>{pageContent}</ParentShell>;
  // Curieux : garde sa navigation (avant, « Retour » l'envoyait vers /tutor/dashboard).
  if (profile.role === 'player') return <PlayerShell>{pageContent}</PlayerShell>;

  return pageContent;
}
